import { useState, useMemo, useEffect, useRef } from "react";
import useHome from "../../custome_hooks/home/useHome";
import { FaSitemap } from "react-icons/fa";
import { CiSettings } from "react-icons/ci";
import { MdOutlineSensors } from "react-icons/md";
import { HiOutlineSignal, HiOutlineSignalSlash } from "react-icons/hi2";
import { FiZap, FiChevronDown, FiAlertTriangle, FiSearch, FiX, FiCheck, FiActivity } from "react-icons/fi";
import { getSensorStatus, formatResistance, isOpenLoop } from "../../utils/sensorStatus";
import { useWebSocketSensor } from "../../custome_hooks/sensors/useWebSocketSensor";
import { formatDisplayName } from "../../utils/textUtils";

// ─── Stat Card Component ───────────────────────────────────────────────────

interface StatCardProps {
  header: string;
  count: number | string;
  subtitle: string;
  Icon: any;
  theme: "blue" | "purple" | "cyan" | "emerald" | "amber" | "rose";
  onClick?: () => void;
}

const themeStyles = {
  blue: {
    bg: "linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(59,130,246,0.22)",
    hoverBorder: "rgba(59,130,246,0.55)",
    iconBg: "rgba(37,99,235,0.2)",
    iconColor: "text-blue-400",
    glow: "rgba(59,130,246,0.18)",
    barBg: "linear-gradient(90deg, #2563eb, #60a5fa)",
  },
  purple: {
    bg: "linear-gradient(135deg, rgba(147,51,234,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(168,85,247,0.22)",
    hoverBorder: "rgba(168,85,247,0.55)",
    iconBg: "rgba(147,51,234,0.2)",
    iconColor: "text-purple-400",
    glow: "rgba(168,85,247,0.18)",
    barBg: "linear-gradient(90deg, #9333ea, #c084fc)",
  },
  cyan: {
    bg: "linear-gradient(135deg, rgba(6,182,212,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(6,182,212,0.22)",
    hoverBorder: "rgba(6,182,212,0.55)",
    iconBg: "rgba(6,182,212,0.2)",
    iconColor: "text-cyan-400",
    glow: "rgba(6,182,212,0.18)",
    barBg: "linear-gradient(90deg, #0891b2, #22d3ee)",
  },
  emerald: {
    bg: "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(16,185,129,0.22)",
    hoverBorder: "rgba(16,185,129,0.55)",
    iconBg: "rgba(16,185,129,0.2)",
    iconColor: "text-emerald-400",
    glow: "rgba(16,185,129,0.18)",
    barBg: "linear-gradient(90deg, #059669, #34d399)",
  },
  amber: {
    bg: "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(245,158,11,0.22)",
    hoverBorder: "rgba(245,158,11,0.55)",
    iconBg: "rgba(245,158,11,0.2)",
    iconColor: "text-amber-400",
    glow: "rgba(245,158,11,0.18)",
    barBg: "linear-gradient(90deg, #d97706, #fbbf24)",
  },
  rose: {
    bg: "linear-gradient(135deg, rgba(244,63,94,0.12) 0%, rgba(15,23,42,0.65) 100%)",
    border: "rgba(244,63,94,0.22)",
    hoverBorder: "rgba(244,63,94,0.55)",
    iconBg: "rgba(244,63,94,0.2)",
    iconColor: "text-rose-400",
    glow: "rgba(244,63,94,0.18)",
    barBg: "linear-gradient(90deg, #e11d48, #f43f5e)",
  },
};

const StatCard: React.FC<StatCardProps> = ({ header, count, subtitle, Icon, theme, onClick }) => {
  const style = themeStyles[theme];

  return (
    <div
      onClick={onClick}
      className={`relative rounded-xl sm:rounded-2xl p-2.5 sm:p-3 lg:p-2.5 xl:p-3.5 2xl:p-4 ${
        onClick ? "cursor-pointer" : "cursor-default"
      } overflow-hidden transition-all duration-300 group hover:-translate-y-1`}
      style={{
        background: style.bg,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${style.border}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
      }}
    >
      {/* Subtle background dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "16px 16px" }}
      />

      {/* Hover glow highlight */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{ boxShadow: `inset 0 0 28px ${style.glow}` }}
      />

      {/* Accent line at top */}
      <div
        className="absolute top-0 left-3 right-3 h-[2px] rounded-full opacity-60 group-hover:opacity-100 transition-opacity"
        style={{ background: style.barBg }}
      />

      <div className="relative z-10 flex items-start justify-between gap-1.5 sm:gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-slate-400 text-[9.5px] sm:text-[10px] xl:text-[11px] font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">
            {header}
          </p>
          <p className="text-lg sm:text-xl lg:text-xl xl:text-2xl 2xl:text-3xl font-black font-mono text-white tracking-tight leading-tight">
            {count}
          </p>
          <p className="text-slate-400 text-[9px] sm:text-[10px] xl:text-[11px] font-medium mt-0.5 truncate">
            {subtitle}
          </p>
        </div>
        <div
          className="rounded-lg sm:rounded-xl p-1.5 sm:p-2 lg:p-1.5 xl:p-2 2xl:p-2.5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
          style={{ background: style.iconBg, border: `1px solid ${style.border}` }}
        >
          <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5 xl:w-4.5 xl:h-4.5 ${style.iconColor}`} />
        </div>
      </div>
    </div>
  );
};

// ─── Futuristic SVG Gauge Ring ─────────────────────────────────────────────

export const GAUGE_STATUS_COLORS = {
  Active: {
    startColor: "#10b981", // emerald-500
    endColor: "#34d399",   // emerald-400
    trackColor: "rgba(16,185,129,0.12)",
  },
  Critical: {
    startColor: "#f59e0b", // amber-500
    endColor: "#fbbf24",   // amber-400
    trackColor: "rgba(245,158,11,0.12)",
  },
  Inactive: {
    startColor: "#f43f5e", // rose-500
    endColor: "#fb7185",   // rose-400
    trackColor: "rgba(244,63,94,0.12)",
  },
};

interface GaugeProps {
  label: string;
  value: number;
  displayValue?: string;
  max: number;
  unit: string;
  gradientId: string;
  startColor: string;
  endColor: string;
  trackColor: string;
  Icon?: any;
  statusBadge?: string;
  statusColor?: string;
}

const GaugeRing: React.FC<GaugeProps> = ({
  label,
  value,
  displayValue,
  max,
  unit,
  gradientId,
  startColor,
  endColor,
  trackColor,
  Icon = FiZap,
  statusBadge,
  statusColor,
}) => {
  const radius = 68;
  const strokeW = 10;
  const circumference = 2 * Math.PI * radius;
  const safeValue = Number.isFinite(value) ? value : 0;
  const pct = displayValue === "OL" ? 1 : Math.min(Math.max(safeValue / max, 0), 1);
  const dashOffset = circumference * (1 - pct);

  return (
    <div
      className="flex flex-col items-center justify-between p-2.5 sm:p-3 lg:p-2.5 xl:p-3 min-[1440px]:p-3.5 2xl:p-4 rounded-2xl group relative overflow-hidden flex-1 min-w-0 transition-all duration-300 hover:border-white/20 hover:shadow-xl"
      style={{
        background: "linear-gradient(150deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 24px rgba(0,0,0,0.3)",
      }}
    >
      {/* Top subtle highlight line */}
      <div
        className="absolute top-0 left-4 right-4 h-[2px] rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${startColor}, transparent)` }}
      />

      {/* Card Header */}
      <div className="w-full flex items-center justify-between relative z-10 mb-1">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div
            className="w-5 h-5 sm:w-6 sm:h-6 rounded-md sm:rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ background: `${startColor}1a`, border: `1px solid ${startColor}35` }}
          >
            <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: startColor }} />
          </div>
          <span className="text-[10px] sm:text-xs min-[1440px]:text-xs 2xl:text-sm font-bold uppercase tracking-wider text-slate-300">
            {label}
          </span>
        </div>
        {statusBadge && (
          <span
            className="text-[8.5px] sm:text-[9.5px] xl:text-[10px] uppercase font-bold tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full border whitespace-nowrap"
            style={{
              color: statusColor || startColor,
              borderColor: `${statusColor || startColor}35`,
              background: `${statusColor || startColor}15`,
            }}
          >
            {statusBadge}
          </span>
        )}
      </div>

      {/* Circle SVG Gauge Ring - Fluid & Responsive diameter across all screens */}
      <div className="relative flex items-center justify-center my-auto py-1 sm:py-1.5 z-10 w-full max-w-[120px] sm:max-w-[135px] md:max-w-[150px] lg:max-w-[115px] xl:max-w-[145px] min-[1440px]:max-w-[165px] 2xl:max-w-[185px] aspect-square">
        {/* Ambient radial glow */}
        <div
          className="absolute inset-2 rounded-full opacity-25 blur-md transition-opacity duration-300 group-hover:opacity-40"
          style={{ background: `radial-gradient(circle, ${startColor} 0%, transparent 70%)` }}
        />

        <svg viewBox="0 0 160 160" className="w-full h-full" style={{ transform: "rotate(-90deg)" }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={startColor} />
              <stop offset="100%" stopColor={endColor} />
            </linearGradient>
          </defs>
          {/* Inner concentric subtle tick ring */}
          <circle
            cx="80"
            cy="80"
            r={radius - 13}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1.5"
            strokeDasharray="2, 6"
          />
          {/* Background Track */}
          <circle cx="80" cy="80" r={radius} fill="none" stroke={trackColor} strokeWidth={strokeW} />
          {/* Animated Gauge Ring */}
          <circle
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeW}
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={dashOffset}
            style={{
              transition: "stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
              filter: `drop-shadow(0 0 8px ${startColor}80)`,
            }}
          />
        </svg>

        {/* Central Value Readout */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-xl sm:text-2xl lg:text-xl xl:text-2xl min-[1440px]:text-3xl 2xl:text-4xl font-black font-mono text-white tracking-tight drop-shadow-md">
            {displayValue ?? safeValue.toFixed(1)}
          </span>
          <span
            className="text-[9px] sm:text-[10px] min-[1440px]:text-xs 2xl:text-sm font-bold uppercase tracking-widest mt-0.5"
            style={{ color: startColor }}
          >
            {unit}
          </span>
        </div>
      </div>

      {/* Progress Bar & Range Limit */}
      <div className="w-full mt-1 min-[1440px]:mt-2 relative z-10">
        <div className="flex justify-between items-center mb-1 text-[9.5px] sm:text-[10.5px] min-[1440px]:text-xs">
          <span className="text-slate-400 font-medium">0 {unit}</span>
          <span className="font-mono font-bold text-slate-200">
            {displayValue ?? safeValue.toFixed(1)} / {max} <span className="text-slate-400 font-normal">{unit}</span>
          </span>
          <span className="text-slate-400 font-medium">{max} {unit}</span>
        </div>
        <div
          className="w-full h-1.5 min-[1440px]:h-2 rounded-full overflow-hidden"
          style={{ background: "rgba(0,0,0,0.5)", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.7)" }}
        >
          <div
            className="h-full rounded-full transition-all duration-1000 relative"
            style={{ width: `${pct * 100}%`, background: `linear-gradient(90deg, ${startColor}, ${endColor})` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Sensor Status Row ─────────────────────────────────────────────────────

interface SensorItem {
  name: string;
  lastUpdatedAt?: string;
  r?: number | string | null;
  g?: number | null;
  v?: number | null;
  i?: number | null;
  [key: string]: any;
}

interface SensorRowProps {
  sensor: SensorItem;
  idx: number;
  isSelected?: boolean;
  onSelect?: () => void;
}

const SensorRow: React.FC<SensorRowProps> = ({ sensor, idx, isSelected, onSelect }) => {
  let lastUpdated = null;
  if (sensor.lastUpdatedAt) {
    let dateValue: number | string = sensor.lastUpdatedAt;
    if (typeof dateValue === "number" || !isNaN(Number(dateValue))) {
      let num = Number(dateValue);
      if (num < 20000000000) num *= 1000;
      dateValue = num;
    }
    lastUpdated = new Date(dateValue);
  }

  const now = new Date();
  let minutesAgo: number | null = null;
  if (lastUpdated && !isNaN(lastUpdated.getTime())) {
    minutesAgo = Math.round((now.getTime() - lastUpdated.getTime()) / 60000);
  }

  const status = getSensorStatus(sensor.r, sensor.lastUpdatedAt, sensor.g, sensor.criticalThreshold);
  const isActive = status === "Active";
  const isCritical = status === "Critical";

  let dotColor = "bg-rose-500";
  let sparkColor = "#f43f5e";
  let badgeClass = "bg-rose-500/10 text-rose-400 border-rose-500/20";

  if (isActive) {
    dotColor = "bg-emerald-400";
    sparkColor = "#34d399";
    badgeClass = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
  } else if (isCritical) {
    dotColor = "bg-amber-400";
    sparkColor = "#fbbf24";
    badgeClass = "bg-amber-500/10 text-amber-400 border-amber-500/20";
  }

  const sparks = useMemo(() => {
    const seed = sensor.name || String(idx);
    return Array.from({ length: 8 }, (_, i) => {
      const s = seed.charCodeAt(i % seed.length) + i * 31;
      return 25 + (s % 65);
    });
  }, [sensor.name, idx]);

  return (
    <div
      onClick={onSelect}
      className={`flex items-center justify-between px-2.5 sm:px-3 min-[1440px]:px-2.5 2xl:px-3.5 py-1.5 sm:py-2 min-[1440px]:py-1.5 2xl:py-2.5 rounded-xl transition-all duration-200 cursor-pointer group border ${
        isSelected
          ? "bg-blue-600/15 border-blue-500/50 shadow-sm"
          : "border-transparent hover:border-white/10 hover:bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
        <div className="relative flex-shrink-0">
          <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
          {(isActive || isCritical) && (
            <div className={`absolute inset-0 rounded-full ${dotColor} animate-ping opacity-50`} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={`text-xs sm:text-sm min-[1440px]:text-xs font-semibold truncate transition-colors ${
              isSelected ? "text-blue-300" : "text-slate-200 group-hover:text-blue-400"
            }`}
          >
            {formatDisplayName(sensor.name)}
          </p>
          {sensor.r != null && (
            <p className="text-[9.5px] sm:text-[10.5px] min-[1440px]:text-[10px] text-slate-400 font-mono flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-slate-300">R: {formatResistance(sensor.r)}</span>
              <span className="text-slate-600">•</span>
              <span
                className={isCritical ? "text-amber-400 font-semibold" : "text-slate-400"}
                title="Site Critical Threshold"
              >
                Limit: {sensor.criticalThreshold != null ? `${Number(sensor.criticalThreshold).toFixed(1)} Ω` : "3.0 Ω"}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 2xl:gap-2.5 flex-shrink-0 ml-2">
        {/* Dynamic Sparkline - Shown on sm: (tablet) and xl: (wide laptop), hidden on lg (1024px) to give max room to text */}
        <div className="hidden sm:flex lg:hidden xl:flex min-[1440px]:hidden 2xl:flex items-end gap-px h-4 w-7 xl:w-8 2xl:w-10 opacity-70 group-hover:opacity-100 transition-opacity">
          {sparks.map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm"
              style={{ height: `${h}%`, background: sparkColor, opacity: 0.4 + (i / sparks.length) * 0.6 }}
            />
          ))}
        </div>

        {minutesAgo === null ? (
          <span className="text-[9px] sm:text-[10px] min-[1440px]:text-[9px] px-1.5 sm:px-2 py-0.5 rounded-full bg-slate-800 text-slate-500 border border-slate-700/60 font-medium whitespace-nowrap">
            No data
          </span>
        ) : (
          <span className={`text-[8.5px] sm:text-[9.5px] min-[1440px]:text-[9px] 2xl:text-[10px] uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full border font-bold whitespace-nowrap ${badgeClass}`}>
            {status}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Searchable Custom Sensor Dropdown Component ───────────────────────────

interface CustomSensorDropdownProps {
  sensors: any[];
  selectedSensor: string;
  onSelectSensor: (sensorName: string) => void;
}

const CustomSensorDropdown: React.FC<CustomSensorDropdownProps> = ({
  sensors,
  selectedSensor,
  onSelectSensor,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSensors = useMemo(() => {
    if (!search.trim()) return sensors;
    const term = search.toLowerCase();
    return sensors.filter((s) => {
      const displayName = formatDisplayName(s.name || "").toLowerCase();
      const rawName = (s.name || "").toLowerCase();
      return displayName.includes(term) || rawName.includes(term);
    });
  }, [sensors, search]);

  const activeObj = useMemo(() => {
    return sensors.find((s) => s.name === selectedSensor);
  }, [sensors, selectedSensor]);

  const activeStatus = activeObj
    ? getSensorStatus(activeObj.r, activeObj.lastUpdatedAt, activeObj.g, activeObj.criticalThreshold)
    : "Active";

  let activeDotColor = "bg-emerald-400";
  if (activeStatus === "Critical") activeDotColor = "bg-amber-400";
  if (activeStatus === "Inactive") activeDotColor = "bg-rose-500";

  return (
    <div className="relative z-50 w-full sm:w-auto" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2.5 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs font-semibold text-white transition-all duration-200 cursor-pointer group hover:bg-white/10"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: isOpen ? "1px solid rgba(59,130,246,0.6)" : "1px solid rgba(255,255,255,0.12)",
          boxShadow: isOpen ? "0 0 16px rgba(59,130,246,0.25)" : "0 2px 10px rgba(0,0,0,0.3)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full ${activeDotColor} shrink-0`} />
          <span className="truncate max-w-[180px] sm:max-w-[190px] text-slate-200 group-hover:text-white font-medium">
            {selectedSensor ? formatDisplayName(selectedSensor) : "Select Sensor"}
          </span>
        </div>
        <FiChevronDown
          className={`w-4 h-4 text-blue-400 shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm rounded-2xl overflow-hidden shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-150"
          style={{
            background: "#0b1325",
            border: "1px solid rgba(59,130,246,0.3)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.8), 0 0 25px rgba(59,130,246,0.2)",
            backdropFilter: "blur(24px)",
          }}
        >
          {/* Search Bar */}
          <div className="p-3 border-b border-white/10 relative flex items-center bg-slate-900/80">
            <FiSearch className="absolute left-6 text-slate-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search earth pits..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full text-xs pl-8 pr-8 py-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500/50 bg-white/[0.06] border border-white/10"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-6 text-slate-400 hover:text-white transition-colors"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto custom-scrollbar p-1.5 space-y-1">
            {filteredSensors.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-500">
                No earth pits found
              </div>
            ) : (
              filteredSensors.map((s: any) => {
                const status = getSensorStatus(s.r, s.lastUpdatedAt, s.g, s.criticalThreshold);
                const isSelected = s.name === selectedSensor;

                let dotColor = "bg-rose-500";
                if (status === "Active") dotColor = "bg-emerald-400";
                if (status === "Critical") dotColor = "bg-amber-400";

                return (
                  <button
                    key={s.name || s.id}
                    type="button"
                    onClick={() => {
                      onSelectSensor(s.name);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs transition-all duration-150 cursor-pointer ${
                      isSelected
                        ? "bg-blue-600/25 text-white font-bold border border-blue-500/40"
                        : "text-slate-300 hover:bg-white/[0.06] hover:text-white border border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                      <span className="truncate">{formatDisplayName(s.name)}</span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-[10px] text-slate-400 font-mono">
                        {s.r != null ? formatResistance(s.r) : status}
                      </span>
                      {isSelected && <FiCheck className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────

const Home = () => {
  const { dashBoardData, sensorsData, navigate } = useHome();

  const [selectedSensor, setSelectedSensor] = useState("");
  const [sensorList, setSensorList] = useState<any[]>([]);
  const [pitSearch, setPitSearch] = useState("");
  const [pitFilter, setPitFilter] = useState<"all" | "Active" | "Critical" | "Inactive">("all");

  useEffect(() => {
    if (Array.isArray(sensorsData) && sensorsData.length > 0) {
      setSensorList(sensorsData);
    }
  }, [sensorsData]);

  useWebSocketSensor(setSensorList);

  useEffect(() => {
    if (sensorList.length > 0) {
      if (!selectedSensor || !sensorList.some((s) => s.name === selectedSensor)) {
        setSelectedSensor(sensorList[0].name);
      }
    }
  }, [sensorList, selectedSensor]);

  const activeSensorData = useMemo(() => {
    return sensorList.find((s) => s.name === selectedSensor) || null;
  }, [sensorList, selectedSensor]);

  const vRaw = activeSensorData?.v || activeSensorData?.V || 0;
  const iRaw = activeSensorData?.i || activeSensorData?.I || 0;
  const rRaw = activeSensorData?.r ?? activeSensorData?.R ?? null;

  const voltage = parseFloat(vRaw) || 0;
  const current = parseFloat(iRaw) || 0;
  const isOl = isOpenLoop(rRaw);
  const resistance = isOl ? 0 : (parseFloat(rRaw) || 0);

  const totalSites = dashBoardData?.sites || 0;
  const totalSystems = dashBoardData?.systems || 0;
  const totalSensors = dashBoardData?.sensors || 0;
  const activeCount = useMemo(() => sensorList.filter((s) => getSensorStatus(s.r, s.lastUpdatedAt, s.g, s.criticalThreshold) === "Active").length, [sensorList]);
  const criticalCount = useMemo(() => sensorList.filter((s) => getSensorStatus(s.r, s.lastUpdatedAt, s.g, s.criticalThreshold) === "Critical").length, [sensorList]);
  const inactiveCount = useMemo(() => sensorList.filter((s) => getSensorStatus(s.r, s.lastUpdatedAt, s.g, s.criticalThreshold) === "Inactive").length, [sensorList]);

  const displayedSensors = useMemo(() => {
    return sensorList.filter((sensor) => {
      const status = getSensorStatus(sensor.r, sensor.lastUpdatedAt, sensor.g, sensor.criticalThreshold);
      if (pitFilter !== "all" && status !== pitFilter) {
        return false;
      }
      if (pitSearch.trim()) {
        const term = pitSearch.toLowerCase();
        const raw = (sensor.name || "").toLowerCase();
        const disp = formatDisplayName(sensor.name || "").toLowerCase();
        return raw.includes(term) || disp.includes(term);
      }
      return true;
    });
  }, [sensorList, pitFilter, pitSearch]);

  const activeSensorStatus = activeSensorData 
    ? getSensorStatus(activeSensorData.r, activeSensorData.lastUpdatedAt, activeSensorData.g, activeSensorData.criticalThreshold) 
    : "Inactive";
  const gaugeColors = GAUGE_STATUS_COLORS[activeSensorStatus as keyof typeof GAUGE_STATUS_COLORS] || GAUGE_STATUS_COLORS.Inactive;

  const criticalLimit = activeSensorData?.criticalThreshold != null ? Number(activeSensorData.criticalThreshold) : 3.0;
  const isResistanceCritical = isOl || (resistance >= criticalLimit);

  const gauges: GaugeProps[] = [
    {
      label: "Voltage",
      value: voltage,
      max: 300,
      unit: "V",
      gradientId: "volGrad",
      Icon: FiZap,
      statusBadge: "Live",
      ...gaugeColors,
    },
    {
      label: "Current",
      value: current,
      max: 50,
      unit: "A",
      gradientId: "curGrad",
      Icon: FiActivity,
      statusBadge: "Live",
      ...gaugeColors,
    },
    {
      label: "Resistance",
      value: isOl ? 500 : resistance,
      displayValue: isOl ? "OL" : undefined,
      max: 500,
      unit: isOl ? "" : "Ω",
      gradientId: "resGrad",
      Icon: MdOutlineSensors,
      statusBadge: isResistanceCritical ? "Critical" : "Normal",
      statusColor: isResistanceCritical ? "#f43f5e" : "#10b981",
      ...gaugeColors,
    },
  ];

  return (
    <div
      className="flex flex-col p-3 sm:p-4 lg:p-4 xl:p-4.5 2xl:p-5 text-white min-h-[calc(100vh-64px)] flex-1 w-full max-w-full relative justify-between overflow-x-hidden"
      style={{ background: "linear-gradient(180deg, #090e1c 0%, #060a14 100%)" }}
    >
      {/* Ambient soft background glows — clipped to prevent horizontal scroll */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-32 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      </div>

      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3.5 sm:mb-4 lg:mb-4 xl:mb-4.5 gap-2.5 sm:gap-4 relative z-10">
        <div>
          <div className="flex items-center gap-2 sm:gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
            <h1 className="text-lg sm:text-xl md:text-2xl font-black text-white tracking-tight">
              Dashboard Overview
            </h1>
          </div>
          <p className="text-slate-400 text-[11px] sm:text-xs md:text-sm mt-0.5">
            Real-time monitoring and earth pit diagnostics
          </p>
        </div>

        {/* Status chip banner */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div className="flex items-center gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-slate-900/70 border border-white/10 backdrop-blur-md shadow-sm text-[11px] sm:text-xs">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-slate-300 font-medium whitespace-nowrap">Monitoring Active</span>
            <span className="text-slate-600">•</span>
            <span className="text-emerald-400 font-mono font-semibold whitespace-nowrap">
              {activeCount}/{sensorList.length} Healthy
            </span>
          </div>
        </div>
      </div>

      {/* Stat Cards Grid — Exactly 6 columns on lg (1024px+) to keep layout sleek and compact */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-2.5 lg:gap-2.5 xl:gap-3 mb-3 sm:mb-3.5 lg:mb-3.5 xl:mb-4 relative z-10">
        <StatCard header="Sites" count={totalSites} subtitle="Registered sites" Icon={FaSitemap} theme="blue" onClick={() => navigate("/site-list")} />
        <StatCard header="Systems" count={totalSystems} subtitle="Monitored systems" Icon={CiSettings} theme="purple" onClick={() => navigate("/system-list")} />
        <StatCard header="Earth Pits" count={totalSensors} subtitle="Installed sensors" Icon={MdOutlineSensors} theme="cyan" onClick={() => navigate("/sensor")} />
        <StatCard header="Active Pits" count={activeCount} subtitle="Normal readings" Icon={HiOutlineSignal} theme="emerald" onClick={() => navigate("/sensor")} />
        <StatCard header="Critical" count={criticalCount} subtitle="High resistance" Icon={FiAlertTriangle} theme="amber" onClick={() => navigate("/sensor")} />
        <StatCard header="Inactive" count={inactiveCount} subtitle="No response / Off" Icon={HiOutlineSignalSlash} theme="rose" onClick={() => navigate("/sensor")} />
      </div>

      {/* Main Panels Section: Gauges + Earth Pit Status */}
      <div className="grid grid-cols-1 lg:grid-cols-5 min-[1440px]:grid-cols-12 gap-3 sm:gap-3.5 lg:gap-3.5 xl:gap-4 relative z-10 items-stretch lg:flex-1 lg:min-h-0 min-[1440px]:min-h-[380px]">
        {/* Live Gauges Panel — 3 cols on lg, 7 cols on >=1440px */}
        <div
          className="lg:col-span-3 min-[1440px]:col-span-7 rounded-2xl flex flex-col relative z-20 overflow-hidden h-auto lg:h-full lg:flex-1"
          style={{
            background: "linear-gradient(145deg, rgba(13,21,38,0.85) 0%, rgba(9,15,30,0.92) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* Header */}
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 px-3.5 sm:px-4 lg:px-4 xl:px-5 py-2.5 sm:py-3.5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-2 sm:gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
              </span>
              <h2 className="text-xs sm:text-sm lg:text-sm xl:text-base font-bold text-white tracking-tight whitespace-nowrap">Live Telemetry Gauges</h2>
              <span className="hidden sm:inline-flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Realtime
              </span>
            </div>

            {/* Custom Searchable Sensor Selector Dropdown */}
            <CustomSensorDropdown
              sensors={sensorList}
              selectedSensor={selectedSensor}
              onSelectSensor={setSelectedSensor}
            />
          </div>

          {/* Subheader showing site limit for selected sensor */}
          {activeSensorData && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 px-3.5 sm:px-4 lg:px-4 xl:px-5 py-2 sm:py-2.5 bg-slate-950/40 border-b border-white/[0.06] text-[11px] sm:text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-slate-400 truncate">
                  Pit: <strong className="text-white">{formatDisplayName(activeSensorData.name)}</strong>
                </span>
                <span
                  className="text-[8.5px] sm:text-[9px] uppercase font-bold tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full border whitespace-nowrap"
                  style={{
                    color: gaugeColors.startColor,
                    borderColor: `${gaugeColors.startColor}35`,
                    background: `${gaugeColors.startColor}15`,
                  }}
                >
                  {activeSensorStatus}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-slate-400">Critical Limit:</span>
                <span className="text-amber-300 font-mono font-bold bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded text-[11px]">
                  {criticalLimit.toFixed(1)} Ω
                </span>
                {isResistanceCritical ? (
                  <span className="text-rose-400 font-bold flex items-center gap-1 bg-rose-500/15 border border-rose-500/30 px-1.5 py-0.5 rounded text-[10.5px] animate-pulse">
                    <FiAlertTriangle className="w-3 h-3" /> Exceeds Safe Limit
                  </span>
                ) : (
                  <span className="text-emerald-400 font-medium flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-[10.5px]">
                    <FiCheck className="w-3 h-3" /> Normal
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Gauge Rings — Flex-1 expands proportionately on lg+ to eliminate bottom void */}
          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-2.5 sm:gap-3 lg:gap-2.5 xl:gap-3 p-2.5 sm:p-3.5 lg:p-3 xl:p-3.5 min-[1440px]:p-4 lg:flex-1 min-[1440px]:justify-between">
            {gauges.map((g) => (
              <GaugeRing key={g.label} {...g} />
            ))}
          </div>

          {/* Telemetry Diagnostics Footer */}
          <div className="px-3.5 sm:px-4 lg:px-4 xl:px-5 py-2 sm:py-2.5 bg-slate-950/60 border-t border-white/[0.06] flex flex-wrap items-center justify-between text-[11px] sm:text-xs text-slate-400 gap-2">
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
              <span className="flex items-center gap-1">
                <FiZap className="w-3 h-3 text-blue-400" /> V: <strong className="text-white font-mono">{voltage.toFixed(1)}V</strong>
              </span>
              <span className="text-slate-600 hidden sm:inline">•</span>
              <span className="flex items-center gap-1">
                <FiActivity className="w-3 h-3 text-purple-400" /> I: <strong className="text-white font-mono">{current.toFixed(1)}A</strong>
              </span>
              <span className="text-slate-600 hidden sm:inline">•</span>
              <span className="flex items-center gap-1">
                <MdOutlineSensors className="w-3 h-3 text-cyan-400" /> R: <strong className="text-white font-mono">{formatResistance(rRaw)}</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[10.5px]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-emerald-400 font-medium">Live Feed Online</span>
            </div>
          </div>
        </div>

        {/* Earth Pit Status Panel — 2 cols on lg, 5 cols on >=1440px */}
        <div
          className="lg:col-span-2 min-[1440px]:col-span-5 rounded-2xl overflow-hidden flex flex-col h-auto lg:h-full lg:flex-1"
          style={{
            background: "linear-gradient(145deg, rgba(13,21,38,0.85) 0%, rgba(9,15,30,0.92) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* Header */}
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3.5 sm:px-4 lg:px-3.5 xl:px-5 py-2.5 sm:py-3.5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <h2 className="text-xs sm:text-sm lg:text-sm xl:text-base font-bold text-white tracking-tight whitespace-nowrap">Earth Pit Status</h2>
            </div>
            {/* Interactive filter badges */}
            <div className="flex flex-wrap items-center gap-1 text-[10px] sm:text-xs">
              <button
                type="button"
                onClick={() => setPitFilter(pitFilter === "Active" ? "all" : "Active")}
                className={`badge-green cursor-pointer transition-all px-2 py-0.5 text-[9.5px] sm:text-[10px] xl:text-[11px] whitespace-nowrap ${
                  pitFilter === "Active" ? "ring-2 ring-emerald-400 bg-emerald-500/25 font-bold" : "hover:bg-emerald-500/20"
                }`}
                title="Filter active pits"
              >
                {activeCount} Normal
              </button>
              <button
                type="button"
                onClick={() => setPitFilter(pitFilter === "Critical" ? "all" : "Critical")}
                className={`badge-amber cursor-pointer transition-all px-2 py-0.5 text-[9.5px] sm:text-[10px] xl:text-[11px] whitespace-nowrap ${
                  pitFilter === "Critical" ? "ring-2 ring-amber-400 bg-amber-500/25 font-bold" : "hover:bg-amber-500/20"
                }`}
                title="Filter critical pits"
              >
                {criticalCount} Critical
              </button>
              <button
                type="button"
                onClick={() => setPitFilter(pitFilter === "Inactive" ? "all" : "Inactive")}
                className={`badge-red cursor-pointer transition-all px-2 py-0.5 text-[9.5px] sm:text-[10px] xl:text-[11px] whitespace-nowrap ${
                  pitFilter === "Inactive" ? "ring-2 ring-rose-400 bg-rose-500/25 font-bold" : "hover:bg-rose-500/20"
                }`}
                title="Filter inactive pits"
              >
                {inactiveCount} Inactive
              </button>
            </div>
          </div>

          {/* Subheader summary bar with search filter */}
          <div className="flex items-center justify-between px-3.5 sm:px-4 lg:px-3.5 xl:px-5 py-2 sm:py-2.5 bg-slate-950/40 border-b border-white/[0.06] text-xs gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-slate-400 truncate text-[10.5px] sm:text-[11px]">
                Monitored: <strong className="text-white font-mono">{sensorList.length}</strong>
              </span>
              {pitFilter !== "all" && (
                <button
                  type="button"
                  onClick={() => setPitFilter("all")}
                  className="text-[9.5px] text-blue-400 hover:text-blue-300 font-medium underline cursor-pointer whitespace-nowrap"
                >
                  Clear ({displayedSensors.length})
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Quick filter input */}
              <div className="relative flex items-center">
                <FiSearch className="absolute left-2 w-3 h-3 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Filter pits..."
                  value={pitSearch}
                  onChange={(e) => setPitSearch(e.target.value)}
                  className="text-[10.5px] pl-6 pr-5 py-1 rounded-lg bg-white/[0.06] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-blue-400/60 w-24 sm:w-28 xl:w-32 transition-all"
                />
                {pitSearch && (
                  <button
                    type="button"
                    onClick={() => setPitSearch("")}
                    className="absolute right-1.5 text-slate-400 hover:text-white"
                  >
                    <FiX className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>

              <span className="text-slate-400 text-[10.5px] hidden xl:flex items-center gap-1 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                Click to inspect
              </span>
            </div>
          </div>

          {/* List of Earth Pits — On mobile & tablet it has a max height so it scrolls comfortably, on lg+ it expands to fill panel */}
          <div className="p-2 sm:p-2.5 lg:flex-1 min-h-[260px] max-h-[380px] sm:max-h-[440px] lg:max-h-none overflow-y-auto custom-scrollbar space-y-1 min-[1440px]:space-y-0 min-[1440px]:grid min-[1440px]:grid-cols-2 min-[1440px]:gap-2 min-[1440px]:content-start">
            {displayedSensors.length === 0 ? (
              <div className="col-span-2 flex flex-col items-center justify-center py-12 text-slate-500">
                <MdOutlineSensors className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-xs sm:text-sm">No earth pits matching criteria</p>
                {(pitFilter !== "all" || pitSearch) && (
                  <button
                    type="button"
                    onClick={() => {
                      setPitFilter("all");
                      setPitSearch("");
                    }}
                    className="text-xs text-blue-400 hover:underline mt-1.5 cursor-pointer"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            ) : (
              displayedSensors.map((sensor: any, idx: number) => (
                <SensorRow
                  key={sensor.name || idx}
                  sensor={sensor}
                  idx={idx}
                  isSelected={sensor.name === selectedSensor}
                  onSelect={() => setSelectedSensor(sensor.name)}
                />
              ))
            )}
          </div>

          {/* System Status Footer */}
          <div className="px-3.5 sm:px-4 lg:px-4 xl:px-5 py-2 sm:py-2.5 bg-slate-950/60 border-t border-white/[0.06] flex items-center justify-between text-[11px] sm:text-xs text-slate-400">
            <span className="truncate">
              System: <strong className="text-slate-200">IES Diagnostics</strong>
            </span>
            <span className="text-[10px] sm:text-[11px] font-mono text-slate-300 whitespace-nowrap">
              <strong className="text-emerald-400">{activeCount}</strong> / {sensorList.length} Online ({sensorList.length > 0 ? Math.round((activeCount / sensorList.length) * 100) : 0}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
